import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Input,
  Button,
  Text,
  makeStyles,
  tokens,
  Field,
} from '@fluentui/react-components';
import { Mail24Regular, ArrowRight24Regular } from '@fluentui/react-icons';
import { loginRequest } from '../../authConfig';
import { clearAuthRedirectMarker } from '../../utils/authErrorHandler';
import senacLogo from '../../assets/senac.png';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '20px',
  },
  loginCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '440px',
    padding: '48px 40px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    boxShadow: tokens.shadow16,
  },
  logo: {
    width: '180px',
    height: 'auto',
    marginBottom: '32px',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 400,
    color: tokens.colorNeutralForeground3,
    marginBottom: '40px',
    textAlign: 'center',
  },
  form: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  input: {
    width: '100%',
  },
  button: {
    width: '100%',
    marginTop: '8px',
  },
  errorText: {
    fontSize: '12px',
    color: tokens.colorPaletteRedForeground1,
    marginTop: '4px',
  },
  infoText: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    marginTop: '16px',
    textAlign: 'center',
  },
});

export const LoginPage: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (accounts.length > 0) {
      clearAuthRedirectMarker(); // Limpa marcador de redirect apos login bem-sucedido
      navigate('/', { replace: true });
    }
  }, [accounts, navigate]);

  const validateEmail = (email: string): boolean => {
    // Check if email is valid format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor, insira um email válido');
      return false;
    }

    // Check if email domain is @senacsp.edu.br
    if (!email.toLowerCase().endsWith('@senacsp.edu.br')) {
      setError('Apenas emails do domínio @senacsp.edu.br são permitidos');
      return false;
    }

    return true;
  };

  const handleLogin = async () => {
    setError('');

    if (!validateEmail(email)) {
      return;
    }

    setIsLoading(true);

    try {
      await instance.loginPopup({
        ...loginRequest,
        loginHint: email,
      });

      // After successful login, user will be redirected by useEffect
    } catch (error) {
      console.error('Login failed:', error);
      setError('Falha ao realizar login. Por favor, tente novamente.');
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.loginCard}>
        <img src={senacLogo} alt="Senac Logo" className={styles.logo} />

        <Text className={styles.title}>
          Senac School Data Sync 2.0
        </Text>
        <Text className={styles.subtitle}>
          Painel de Monitoramento
        </Text>

        <div className={styles.form}>
          <Field
            label="Email"
            validationMessage={error}
            validationState={error ? 'error' : 'none'}
          >
            <Input
              className={styles.input}
              type="email"
              placeholder="seu.email@senacsp.edu.br"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              contentBefore={<Mail24Regular />}
              size="large"
              disabled={isLoading}
            />
          </Field>

          <Button
            className={styles.button}
            appearance="primary"
            size="large"
            onClick={handleLogin}
            disabled={!email || isLoading}
            icon={<ArrowRight24Regular />}
            iconPosition="after"
          >
            {isLoading ? 'Autenticando...' : 'Avançar'}
          </Button>

          <Text className={styles.infoText}>
            Use suas credenciais institucionais do Senac SP
          </Text>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
